import { FormGroup } from '@angular/forms';
import { DialogButton, DialogData } from '../../../store/models/dialog.model';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  HostListener,
  Inject,
  InjectionToken,
  Injector,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { openDialog } from 'app/store/actions/page.action';
import { Subject } from 'rxjs';
import { ButtonDisablerService } from '../../../store/services/button-disabler.service';
import { ButtonComponent } from '../button/button.component';

export const DIALOG_COMPONENT = new InjectionToken<DialogComponent>(
  'DialogComponent',
);

export type IDialogComponent<T> = T & {
  componentReady?: boolean;
};

@Component({
  selector: 'app-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
})
export class DialogComponent implements OnInit, AfterViewInit {
  @ViewChild('content', { read: ViewContainerRef, static: true })
  content: ViewContainerRef;
  @ViewChild('dialog', { read: ViewContainerRef, static: true })
  dialog: ViewContainerRef;
  @ViewChild('dialogContainer', { read: ViewContainerRef, static: true })
  dialogContainer: ViewContainerRef;
  @ViewChildren('dialogButtons') dialogButtons: QueryList<ButtonComponent>;

  contentAsHtml = true;

  dialogForm: FormGroup;
  showTitleTooltip = false;
  component: IDialogComponent<any>;

  result: any;

  onCloseEvent: Subject<any> = new Subject<any>();

  private triggerClose = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<DialogComponent>,
    private fr: ComponentFactoryResolver,
    private store: Store<IReducers>,
    private injector: Injector,
    private readonly buttonDisablerService: ButtonDisablerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  setButtonDisabled(text: string, disabled: boolean): void {
    const config = this.data.buttons?.find(b => b.text === text);
    if (config) {
      config.disabled = disabled;
    }
    this.dialogButtons?.forEach(btn => {
      if (btn.text === text) {
        btn.disabled = disabled;
        if (disabled) {
          btn.showLoadingIcon = false;
        }
      }
    });
    this.changeDetectorRef.detectChanges();
  }

  close() {
    this.closeDialog();
  }

  onClose() {
    return this.onCloseEvent.asObservable();
  }

  releaseButtons(): void {
    if (this.dialogButtons) {
      this.dialogButtons.toArray().forEach(b => b.release());
    }
  }

  lockButtons(): void {
    if (this.dialogButtons) {
      for (const b of this.dialogButtons.toArray()) {
        b.lock();
      }
    }
  }

  closeDialog() {
    this.triggerClose = !!this.data.onClose;
    this.dialog.element.nativeElement.style.display = 'none';
    this.dialogRef.close();
    this.buttonDisablerService.disableActive();
  }

  onButtonClick(button: DialogButton) {
    if (this.dialogForm && Object.keys(this.dialogForm.controls) && !this.dialogForm.valid) {
      this.releaseButtons();
    }
    if (button.actAsClose === true) {
      this.closeDialog();
    }

    if (button.onClick) {
      button.onClick(this);
    }
  }

  setResult(result: any): void {
    this.result = result;
  }

  onCloseButton() {
    if (this.data.onCloseButton) {
      this.data.onCloseButton(this);
    } else {
      this.closeDialog();
    }
  }

  setButtonsText(text: string, index: number): void {
    let i = 0;
    for (const b of this.dialogButtons.toArray()) {
      if (index === i) {
        b.text = text;
      }
      i++;
    }
  }

  setButtons(buttons: DialogButton[]) {
    if (this.dialogButtons) {
      for (const b of this.dialogButtons.toArray()) {
        for (const bb of buttons) {
          if (b.text === bb.text) {
            bb.id = b.id;
            bb.disabled = b.disabled;
          }
        }
      }
    }
    this.data.buttons = buttons;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const windowHeight = window.innerHeight;
    if (this.dialog) {
      this.dialog.element.nativeElement.style.maxHeight = `${windowHeight}px`;
    }
    if (this.dialogContainer) {
      this.dialogContainer.element.nativeElement.style.maxHeight = `${windowHeight - 132}px`;
    }
  }

  ngOnInit() {
    const windowHeight = window.innerHeight;
    if (this.dialog) {
      this.dialog.element.nativeElement.style.maxHeight = `${windowHeight}px`;
    }
    if (this.dialogContainer) {
      this.dialogContainer.element.nativeElement.style.maxHeight = `${windowHeight - 132}px`;
    }
    if (!this.data.closeButtonText) {
      this.data.closeButtonText = 'Close';
    }
    const cData: any = this.data.componentData;
    if (
      cData !== undefined &&
      cData.data !== undefined &&
      cData.data &&
      cData.data.tag !== undefined &&
      this.data.title !== 'Tips'
    ) {
      this.showTitleTooltip = true;
    }

    if (this.data.hideTitleTooltip) {
      this.showTitleTooltip = false;
    }
    this.dialogRef.afterClosed().subscribe(() => {
      if (this.data.onClose && this.triggerClose) {
        this.data.onClose(this);
      }
    });

    if (typeof this.data.content !== 'string') {
      this.contentAsHtml = false;
      const factory = this.fr.resolveComponentFactory(this.data.content as any);
      const injector = Injector.create({
        parent: this.injector,
        providers: [
          {
            provide: DIALOG_COMPONENT,
            useValue: this,
          },
        ],
      });
      const component = this.content.createComponent(factory, 0, injector);

      if (this.data.componentData) {
        for (const item in this.data.componentData) {
          if (this.data.componentData[item] !== undefined) {
            component.instance[item] = this.data.componentData[item];
          }
        }
      }

      this.dialogForm = new FormGroup({});
      // @ts-ignore
      component.instance.inputForm = this.dialogForm;
      // @ts-ignore
      component.instance.dialogRef = this;
      this.component = component.instance;
      component.changeDetectorRef.detectChanges();
    } else {
      this.contentAsHtml = true;
    }
  }

  ngAfterViewInit(): void {
  }

  openTooltip(type) {
    if (this.data.titleIcon) {
      return;
    }
    this.store.dispatch(openDialog({ dialogData: this.getDialogData(type) }));
  }

  getDialogData(type): DialogData {
    return {
      content: InfoTipsComponent,
      title: 'Tips',
      canClose: true,
      dialogMinWidth: '700px',
      componentData: {
        type,
      },
    };
  }

  getTitleIcon() {
    if (!this.data.titleIcon) {
      return 'Yellow Plane';
    }

    return this.data.titleIcon;
  }

  componentNotReady(): boolean {
    if (!this.component) {
      return false;
    }

    if (typeof this.component.componentReady === 'undefined') {
      return false;
    }

    return !this.component.componentReady;
  }

  get centerButtons(): DialogButton[] {
    return (this.data.buttons || []).filter(button => button.align === 'center');
  }

  get endButtons(): DialogButton[] {
    return (this.data.buttons || []).filter(button => button.align !== 'center');
  }
}
